import { handleActions } from 'redux-actions'
import { compose } from 'recompose'
import { selectorForSlice } from 'lp-redux-utils'
import { createSelector } from 'reselect'
import { setOnSuccess, handleSuccess } from 'lp-redux-api'
import * as LS from 'local-storage'
import * as apiActions from 'api-actions'
import * as schoolActions from 'school-portal-actions'
import { set } from 'utils'
import { toNumber } from 'lodash'
import * as Types from 'types'
import { isAdmin } from '../educator-portal/auth'
import {
  normalizeLearningHubCatalog,
  normalizeLearningHubCourse,
} from 'utils/local/normalize-learning-hub-catalog'

const reducerKey = 'global'
const slice = 'root.learningHub.global'

const initialState = {
  currentSchoolId: Number(LS.getCurrentSchoolId()),
  isAdmin: isAdmin(),
  postsCount: 0,
  postsPageCount: 0,
  posts: null,
  post: null,
  postTopics: null,
  searchOptions: null,
  userMentionSuggestions: null,
  bookmarkedCourses: [],
}

// `data` arrives either as snake_case (server) or camelCase (after lp-requests
// runs its automatic key transform, which depends on per-request config). We
// accept either shape so the player components don't need to care which fetch
// (learner or preview) populated `currentBlock`.
function normalizeBlockPayload(data) {
  if (!data) return null

  const scormRaw = data.scorm
  const scorm =
    scormRaw &&
    Object.assign({}, scormRaw, {
      launcherPath: scormRaw.launcherPath ?? scormRaw.launcher_path,
      launchUrl: scormRaw.launchUrl ?? scormRaw.launch_url,
    })
  const zoomRaw = data.zoom
  const zoom =
    zoomRaw &&
    Object.assign({}, zoomRaw, {
      joinUrl: zoomRaw.joinUrl ?? zoomRaw.join_url,
      startTime: zoomRaw.startTime ?? zoomRaw.start_time,
    })

  return {
    id: data.id,
    chapterId: data.chapterId ?? data.chapter_id,
    blockType: data.blockType ?? data.block_type,
    name: data.name,
    description: data.description,
    estimatedTime: data.estimatedTime ?? data.estimated_time,
    order: data.order,
    attachmentUrl: data.attachmentUrl ?? data.attachment_url,
    scheduledTime: data.scheduledTime ?? data.scheduled_time,
    scorm,
    submitForReview: data.submitForReview ?? data.submit_for_review,
    retestRequired: data.retestRequired ?? data.retest_required,
    retestPercentage: data.retestPercentage ?? data.retest_percentage,
    reviewerComment: data.reviewerComment ?? data.reviewer_comment,
    attemptStatus: data.attemptStatus ?? data.attempt_status,
    progress: data.progress || {},
    ebookContent: data.ebookContent ?? data.ebook_content,
    ebookSavedContent: data.ebookSavedContent ?? data.ebook_saved_content,
    ebookHasSavedContent:
      data.ebookHasSavedContent ?? data.ebook_has_saved_content,
    questions: data.questions,
    savedResponses: data.savedResponses ?? data.saved_responses ?? {},
    savedStatus: data.savedStatus ?? data.saved_status,
    zoom,
  }
}

const reducer = handleActions(
  {
    // Need this set by school action when not set yet in LS (e.g., before sign in)
    [schoolActions.setCurrentSchoolId]: (state, { payload: id }) => {
      if (!id) return set('currentSchoolId', null, state)
      return set('currentSchoolId', toNumber(id), state)
    },
    [apiActions.fetchSchoolAuthorized]: setOnSuccess('school'),
    [apiActions.fetchCurrentEducatorUserDetails]: (
      state,
      { payload: { data } }
    ) => {
      if (!data) return state
      const bookmarks = [
        ...data.postBookmarks,
        ...data.userBookmarks,
        ...(data.learningHubBookmarks || []),
      ]
      return compose(
        set('userDetails', data),
        set('bookmarks', bookmarks),
        set('isAdmin', data.admin === true)
      )(state)
    },
    [apiActions.fetchCurrentArtistUserDetails]: (
      state,
      { payload: { data } }
    ) => {
      if (!data) return state
      const bookmarks = [
        ...(data.postBookmarks || []),
        ...(data.userBookmarks || []),
        ...(data.learningHubBookmarks || []),
      ]
      return compose(
        set('userDetails', data),
        set('bookmarks', bookmarks),
        set('isAdmin', data.admin === true)
      )(state)
    },
    [apiActions.fetchCurrentPartnerUserDetails]: (
      state,
      { payload: { data } }
    ) => {
      if (!data) return state
      const bookmarks = [
        ...(data.postBookmarks || []),
        ...(data.userBookmarks || []),
        ...(data.learningHubBookmarks || []),
      ]
      return compose(
        set('userDetails', data),
        set('bookmarks', bookmarks),
        set('isAdmin', data.admin === true)
      )(state)
    },
    [apiActions.createOrUpdateEducatorUserProfile]: setOnSuccess(
      'userDetails.profile'
    ),
    [apiActions.fetchLearningHubCourses]: setOnSuccess(
      'learningHubCatalog',
      ({ payload: { data } }) => normalizeLearningHubCatalog(data)
    ),
    [apiActions.fetchLearningHubCourse]: setOnSuccess(
      'currentCourse',
      ({ payload: { data } }) => data.course
    ),
    [apiActions.fetchLearningHubCoursePreview]: setOnSuccess(
      'currentCourse',
      ({ payload: { data } }) => data.course
    ),
    // The preview-mode block fetch returns the same payload shape as the
    // learner fetch, so both cases share `normalizeBlockPayload` below.
    [apiActions.fetchLearningHubBlockPreview]: setOnSuccess(
      'currentBlock',
      ({ payload: { data } }) => normalizeBlockPayload(data)
    ),
    [apiActions.fetchLearningHubBlock]: setOnSuccess(
      'currentBlock',
      ({ payload: { data } }) => normalizeBlockPayload(data)
    ),
    [apiActions.fetchLearningHubCoursesMore]: handleSuccess(
      (state, _action, data) => mergeLearningHubCatalogSlice(state, data)
    ),
    [apiActions.fetchLearningHubMyCourses]: (state, { payload: { data } }) => {
      if (!data) return state
      return compose(
        set('myCourses', data.courses || []),
        set(
          'bookmarkedCourses',
          data.bookmarkedCourses || data.bookmarked_courses || []
        )
      )(state)
    },
    [apiActions.fetchNotifications]: setOnSuccess('notifications'),
    [apiActions.fetchSearchOptions]: (state, { payload: { data } }) => {
      if (!data || data.type !== Types.SEARCH_OPTIONS_TYPE.FORUM) return state
      return compose(set('searchOptions', data.searchOptions))(state)
    },
    [apiActions.fetchLearningHubPostTopics]: setOnSuccess('postTopics'),
    [apiActions.fetchMentionSuggestions]: setOnSuccess(
      'userMentionSuggestions'
    ),
    [apiActions.fetchLearningHubDiscussions]: (
      state,
      { payload: { data } }
    ) => {
      if (!data) return state
      const {
        results: { data: posts },
        count,
        totalPages,
      } = data
      return compose(
        set('posts', posts),
        set('postsCount', count),
        set('postsPageCount', totalPages)
      )(state)
    },
    [apiActions.fetchLearningHubDiscussion]: setOnSuccess('post'),
    [apiActions.fetchLearningHubDiscussionComments]:
      setOnSuccess('post.comments'),
    [apiActions.createLearningHubDiscussionComment]: handleSuccess(
      (state, { payload }) => setCommentsState({ state, payload })
    ),
    [apiActions.updateLearningHubDiscussionComment]: handleSuccess(
      (state, { payload }) => setCommentsState({ state, payload })
    ),
    [apiActions.archiveLearningHubDiscussionComment]: handleSuccess(
      (state, { payload }) => setCommentsState({ state, payload })
    ),
    [apiActions.removeLearningHubBookmark]: setOnSuccess(
      'bookmarks',
      ({ payload: { data } }) => data.bookmarks
    ),
    [apiActions.addLearningHubBookmark]: setOnSuccess(
      'bookmarks',
      ({ payload: { data } }) => data.bookmarks
    ),
    [apiActions.addLearningHubLike]: handleSuccess((state, { payload }) =>
      setLikeState({ state, payload })
    ),
    [apiActions.destroyLearningHubLike]: handleSuccess((state, { payload }) =>
      setLikeState({ state, payload })
    ),
    [apiActions.markAllAsRead]: setOnSuccess('notifications'),
    [apiActions.deleteNotification]: setOnSuccess('notifications'),
    [apiActions.markAsRead]: setOnSuccess('notifications'),
    [apiActions.fetchNotificationPreferences]: setOnSuccess(
      'notificationPreferences'
    ),
    [apiActions.updateNotificationPreferences]: setOnSuccess(
      'notificationPreferences'
    ),
  },
  initialState
)

const select = selectorForSlice(slice)

const selectors = {
  currentSchoolId: select('currentSchoolId'),
  isAdmin: select('isAdmin'),
  school: select('school'),
  userDetails: select('userDetails'),
  userId: select('userDetails.id'),
  bookmarks: select('bookmarks', []),
  notifications: select('notifications'),
  notificationPreferences: select('notificationPreferences'),
  learningHubCatalog: select('learningHubCatalog'),
  currentCourse: select('currentCourse'),
  currentBlock: select('currentBlock'),
  postTopics: select('postTopics'),
  userMentionSuggestions: select('userMentionSuggestions'),
  posts: select('posts'),
  postsCount: select('postsCount'),
  postsPageCount: select('postsPageCount'),
  post: select('post'),
  searchOptions: select('searchOptions'),
  myCourses: select('myCourses'),
  bookmarkedCourses: select('bookmarkedCourses', []),
}

selectors.learningHubCourses = createSelector(
  [selectors.learningHubCatalog],
  function (catalog) {
    if (catalog === undefined) return undefined
    return (catalog.disciplineGroups || []).flatMap((g) => g.courses || [])
  }
)

selectors.isModerator = createSelector(
  [selectors.userDetails],
  function (userDetails) {
    if (!userDetails) return
    return Types.MODERATOR_ROLES.includes(userDetails.role)
  }
)

function mergeLearningHubCatalogSlice(state, data) {
  if (!state.learningHubCatalog?.disciplineGroups || !data) return state
  if (!Array.isArray(data.courses) || data.courses.length === 0) return state

  const nextGroups = state.learningHubCatalog.disciplineGroups.map((g) => {
    const dataUncat = Boolean(data.uncategorized)
    const match =
      (dataUncat && g.disciplineId == null) ||
      (!dataUncat && Number(g.disciplineId) === Number(data.disciplineId))

    if (!match) return g

    const existingIds = new Set((g.courses || []).map((c) => Number(c.id)))
    const additions = data.courses
      .filter((c) => !existingIds.has(Number(c.id)))
      .map(normalizeLearningHubCourse)
    if (additions.length === 0) return g

    return {
      ...g,
      courses: [...(g.courses || []), ...additions],
    }
  })

  return set(
    'learningHubCatalog',
    {
      ...state.learningHubCatalog,
      disciplineGroups: nextGroups,
    },
    state
  )
}

function swapPostState(state, newPost) {
  if (!state.posts) return state
  const index = state.posts.findIndex((post) => post.id === newPost.id)
  const updatedPosts = [...state.posts]
  const existingPost = updatedPosts[index]
  updatedPosts[index] = {
    ...existingPost,
    likes: newPost.likes,
  }
  return compose(set('posts', updatedPosts))(state)
}

function isCommentLikeLikeResponse(item) {
  if (!item || typeof item !== 'object') return false
  if (
    item.type === 'comment' ||
    item.type === 'learning_hub_discussion_comment'
  ) {
    return true
  }
  const ct = item.commentableType
  return (
    ct === Types.COMMENTABLE.POST ||
    ct === Types.COMMENTABLE.COMMENT ||
    ct === Types.COMMENTABLE.LEARNING_HUB_DISCUSSION ||
    ct === Types.COMMENTABLE.LEARNING_HUB_COMMENT
  )
}

function setLikeState({ state, payload: { data: likedItem } }) {
  if (!likedItem) return state
  if (isCommentLikeLikeResponse(likedItem))
    return setCommentState(state, likedItem)
  const updatedPostsState = state.posts
    ? swapPostState(state, likedItem)
    : state
  return setPostState(updatedPostsState, likedItem)
}

function setCommentsState({ state, payload: { data: changedComments } }) {
  if (!changedComments) return state

  return compose(
    set('post.comments', changedComments),
    set('post.commentsCount', changedComments.length)
  )(state)
}

function replaceComment(comments, changedComment) {
  return comments.map((comment) => {
    if (comment.id !== changedComment.id) return comment
    return changedComment
  })
}

function getUpdatedComments(stateComments, changedComment) {
  if (
    changedComment.commentableType === Types.COMMENTABLE.POST ||
    changedComment.commentableType === Types.COMMENTABLE.LEARNING_HUB_DISCUSSION
  )
    return replaceComment(stateComments, changedComment)

  return stateComments.map((comment) => {
    if (comment.id !== changedComment.commentableId) return comment

    const threadedComments = [...comment.threadedComments]
    return {
      ...comment,
      threadedComments: replaceComment(threadedComments, changedComment),
    }
  })
}

function setCommentState(state, changedComment) {
  if (!state.post || !state.post.comments) return state

  const stateComments = [...state.post.comments]
  return compose(
    set('post.comments', getUpdatedComments(stateComments, changedComment))
  )(state)
}

function setPostState(state, changedPost) {
  if (!state.post) return state

  const updatedPost = {
    comments: state.post.comments,
    commentsCount: state.post.commentsCount,
    ...changedPost,
  }
  return compose(set('post', updatedPost))(state)
}

export { reducer, reducerKey, selectors }
